// Import the functions you need from the SDKs you need
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, GoogleAuthProvider, initializeRecaptchaConfig } from "firebase/auth";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager, connectFirestoreEmulator } from 'firebase/firestore';
import { getDatabase } from "firebase/database";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { initializeAppCheck, ReCaptchaV3Provider, getToken as getAppCheckToken } from "firebase/app-check";
import { getStorage } from 'firebase/storage';

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAorO6bwPfQDRnRsi6BhKVJ_Xo73K7YYE4",
  authDomain: "students-data-base-e3ecf.firebaseapp.com",
  databaseURL: "https://students-data-base-e3ecf-default-rtdb.europe-west1.firebasedatabase.app/",
  projectId: "students-data-base-e3ecf",
  storageBucket: "students-data-base-e3ecf.firebasestorage.app",
  messagingSenderId: "688649942963",
  appId: "1:688649942963:web:fc9d8f11dc18e12ba6fdc6",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-XLJ9DSKWNL"
};

// Initialize Firebase (prevent duplicate app error)
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

const auth = getAuth(app);
// Replace standard Firestore initialization with offline persistence support
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager()
  })
});
const googleProvider = new GoogleAuthProvider();
const rtdb = getDatabase(app);
const storage = getStorage(app);
// Use europe-west1 to match Functions region
const functions = getFunctions(app, 'europe-west1');

// Emulator connections are opt-in via Vite env flags.
// Use VITE_USE_FIREBASE_EMULATOR=true for Functions and
// VITE_USE_FIRESTORE_EMULATOR=true only when the Firestore emulator is also running.
const USE_EMULATOR = String(import.meta.env?.VITE_USE_FIREBASE_EMULATOR || '').toLowerCase() === 'true';
const USE_FIRESTORE_EMULATOR = String(import.meta.env?.VITE_USE_FIRESTORE_EMULATOR || '').toLowerCase() === 'true';
const APP_CHECK_SITE_KEY = String(import.meta.env?.VITE_RECAPTCHA_V3_SITE_KEY || '').trim();
const APP_CHECK_DEBUG_TOKEN = String(import.meta.env?.VITE_APP_CHECK_DEBUG_TOKEN || '').trim();
const ENABLE_APP_CHECK_DEBUG = import.meta.env.DEV && !USE_EMULATOR && Boolean(APP_CHECK_DEBUG_TOKEN);
const APP_CHECK_TOKEN_TIMEOUT_MS = Number(import.meta.env?.VITE_APP_CHECK_TIMEOUT_MS) > 0
  ? Number(import.meta.env.VITE_APP_CHECK_TIMEOUT_MS)
  : 8000;

let appCheck = null;
let appCheckInitPromise = null;

function initAppCheckLazy() {
  if (appCheck) return;
  if (!APP_CHECK_SITE_KEY) return;
  try {
    if (ENABLE_APP_CHECK_DEBUG) {
      globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN =
        APP_CHECK_DEBUG_TOKEN === 'true' ? true : APP_CHECK_DEBUG_TOKEN;
    } else if ('FIREBASE_APPCHECK_DEBUG_TOKEN' in globalThis) {
      delete globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN;
    }
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    console.warn('[Firebase] Failed to initialize App Check:', e?.message || e);
  }
}

if (typeof window !== 'undefined') {
  // Defer reCAPTCHA and App Check initialization so they don't block
  // initial paint or trigger third-party cookie warnings on page load.
  const deferInit = () => {
    initializeRecaptchaConfig(auth).catch((e) => {
      console.warn('[Firebase] Failed to preload Auth reCAPTCHA config:', e?.message || e);
    });
    initAppCheckLazy();
  };

  if (APP_CHECK_SITE_KEY || !import.meta.env.DEV) {
    // Delay 5s so reCAPTCHA scripts don't compete with LCP resources.
    appCheckInitPromise = new Promise((resolve) => {
      setTimeout(() => { deferInit(); resolve(); }, 5000);
    });
  }

  if (!APP_CHECK_SITE_KEY && !import.meta.env.DEV && !USE_EMULATOR) {
    console.warn('[Firebase] App Check site key missing; callable abuse protections are reduced.');
  }
}

if (USE_EMULATOR) {
  try {
    console.info('[Firebase] Connecting to local emulators (opt-in flag enabled)');
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    if (USE_FIRESTORE_EMULATOR) {
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
    }
  } catch (e) {
    console.warn('[Firebase] Failed to connect to emulators, falling back to cloud:', e?.message || e);
  }
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout(promise, timeoutMs) {
  let timerId;
  const timeoutPromise = new Promise((_, reject) => {
    timerId = setTimeout(() => {
      reject(new Error('App Check token request timed out'));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timerId);
  });
}

export async function waitForAppCheckToken({ forceRefresh = false } = {}) {
  if (USE_EMULATOR || typeof window === 'undefined') {
    return null;
  }

  // Ensure deferred init has completed before requesting a token
  if (appCheckInitPromise) {
    await appCheckInitPromise;
  }
  if (!appCheck) {
    initAppCheckLazy();
  }

  if (!appCheck) {
    throw {
      code: 'APP_CHECK_UNAVAILABLE',
      message: 'Secure app verification is not initialized for this environment.',
    };
  }

  let lastError = null;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const tokenResult = await withTimeout(
        getAppCheckToken(appCheck, forceRefresh || attempt > 0),
        APP_CHECK_TOKEN_TIMEOUT_MS
      );
      const token = String(tokenResult?.token || '').trim();
      if (token) {
        return token;
      }
      lastError = new Error('Empty App Check token');
    } catch (error) {
      lastError = error;
    }

    if (attempt === 0) {
      await delay(300);
    }
  }

  throw {
    code: 'APP_CHECK_UNAVAILABLE',
    message: `Secure app verification failed: ${lastError?.message || 'App Check token unavailable'}`,
  };
}

export { app, auth, db, googleProvider, rtdb, storage, functions, appCheck };

